<template>
  <div class="partner-card" :class="{ 'cta-card': partner.isCallToAction }">
    <div class="partner-image" :class="{ 'cta-image': partner.isCallToAction, 'reel-hoverable': partner.hasTestimonial }" @click="handleClick" @mouseenter="handleMouseEnter">
      <div v-if="partner.isCallToAction" class="question-mark">?</div>
      <template v-else>
        <img 
          :src="partner.image"
          :alt="partner.name + ' - ' + partner.title"
          loading="lazy"
          @error="handleImageError" />
        <div v-if="partner.hasTestimonial" class="reel-overlay" @click="handleClick" @touchstart.stop @touchmove.stop @touchend.stop>
          <span class="reel-icon">▶︎</span>
          <span class="reel-text">{{ $t('partners.watchTestimonial') }}</span>
        </div>
      </template>
    </div>
    <div class="partner-info">
      <h3>{{ partner.name }}</h3>
      <p class="partner-title">{{ partner.title }}</p>
      <p class="partner-followers">{{ partner.followers }}</p>
      <div class="partner-achievements">
        <span v-for="(achievement, index) in partner.achievements"
              :key="index">{{ achievement }}</span>
      </div>
      <a v-if="partner.isCallToAction" 
         href="https://app.reclaim.ai/m/dawid-paliczuk/15-min-call" 
         target="_blank" 
         rel="noopener" 
         class="cta-button">
        {{ $t('partners.bookConsultation') }}
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerCard',
  props: {
    partner: {
      type: Object,
      required: true
    }
  },
  emits: ['open-embed'],
  methods: {
    handleImageError(e) {
      e.target.src = 'https://via.placeholder.com/150x150.png?text=' + this.partner.name.split(' ')[0]
    },
    handleClick() {
      if (this.partner?.hasTestimonial) this.$emit('open-embed')
    },
    handleMouseEnter() {
      if (this.partner?.hasTestimonial) this.$emit('open-embed')
    }
  }
}
</script>

<style scoped>
/* PartnerCard Theme Support */
.partner-card {
  background: var(--glass-bg);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.partner-card:hover {
  transform: translateY(-8px) scale(1.02);
  background: var(--glass-bg);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border-color: var(--accent-green, rgba(0, 255, 179, 0.3));
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
}

.partner-image {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(0, 255, 179, 0.6);
  box-shadow: 0 0 20px rgba(0, 255, 179, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: relative;
}

.partner-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reel-hoverable .reel-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.35);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  opacity: 1;
  transition: opacity 0.25s ease;
  pointer-events: auto;
  cursor: pointer;
}

.reel-hoverable:hover .reel-overlay {
  opacity: 1;
}

.reel-icon {
  font-size: 24px;
  line-height: 1;
}

.reel-text {
  font-size: 0.9rem;
  font-weight: 600;
}

.partner-info h3 {
  font-size: 1.5rem;
  margin: 0 0 5px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.partner-title {
  color: var(--text-secondary);
  margin: 0 0 10px;
  font-size: 1.1rem;
}

.partner-followers {
  color: var(--accent-green, #00ffb3);
  margin: 0 0 15px;
  font-size: 1rem;
  font-weight: 600;
}

.partner-achievements {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.partner-achievements span {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

/* Call-to-Action Card Styles */
.cta-card {
  background: var(--glass-bg);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 2px solid var(--accent-purple, rgba(182, 36, 255, 0.4));
  animation: pulse-glow 3s ease-in-out infinite;
  box-shadow: 0 12px 40px rgba(182, 36, 255, 0.2);
}

.cta-card:hover {
  transform: translateY(-10px) scale(1.03);
  background: var(--glass-bg);
  backdrop-filter: blur(30px) saturate(250%);
  -webkit-backdrop-filter: blur(30px) saturate(250%);
  border-color: var(--accent-purple, rgba(182, 36, 255, 0.6));
  box-shadow: 0 20px 50px rgba(182, 36, 255, 0.3);
}

.cta-image {
  border-color: var(--accent-purple) !important;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-mark {
  font-size: 4rem;
  font-weight: bold;
  color: var(--text-primary);
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
  animation: question-bounce 2s ease-in-out infinite;
}

.cta-button {
  display: inline-block;
  background: var(--gradient-primary);
  color: var(--text-primary);
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: bold;
  font-size: 1rem;
  margin-top: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(182, 36, 255, 0.3);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(182, 36, 255, 0.4);
  text-decoration: none;
  color: var(--text-primary);
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(182, 36, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(182, 36, 255, 0.5);
  }
}

@keyframes question-bounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@media (max-width: 768px) {
  .partner-card {
    padding: 20px;
  }

  .partner-image {
    width: 120px;
    height: 120px;
  }

  .partner-info h3 {
    font-size: 1.3rem;
  }

  .partner-title {
    font-size: 1rem;
  }

  .partner-achievements span {
    font-size: 0.85rem;
  }
  
  .question-mark {
    font-size: 3rem;
  }
  
  .cta-button {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
}
</style> 